import { useCallback, useEffect, useRef, useState } from "react";

export interface PanZoom {
	x: number;
	y: number;
	k: number;
}

const MIN_K = 0.35;
const MAX_K = 2.8;

/** Movement below this after pointerdown counts as a tap, not a pan. */
const TAP_MOVE_PX = 10;

function touchDistance(a: Touch, b: Touch): number {
	return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

export interface UsePanZoomOptions {
	/** Fires when the pointer went up without meaningful drag (reliable “click”). */
	onShortPointerUp?: (detail: { startTarget: Element | null }) => void;
}

export function usePanZoom(initial: PanZoom, options?: UsePanZoomOptions) {
	const [pan, setPan] = useState<PanZoom>(initial);
	const panRef = useRef(pan);
	panRef.current = pan;

	const onShortPointerUpRef = useRef(options?.onShortPointerUp);
	onShortPointerUpRef.current = options?.onShortPointerUp;

	const elRef = useRef<HTMLDivElement | null>(null);
	const dragRef = useRef<{
		active: boolean;
		pointerId: number;
		startX: number;
		startY: number;
		lastX: number;
		lastY: number;
		didDrag: boolean;
		startTarget: Element | null;
	} | null>(null);

	const setKAtPoint = useCallback((nextK: number, anchorX: number, anchorY: number) => {
		setPan((prev) => {
			const k = Math.min(MAX_K, Math.max(MIN_K, nextK));
			const ratio = k / prev.k;
			return {
				k,
				x: anchorX - (anchorX - prev.x) * ratio,
				y: anchorY - (anchorY - prev.y) * ratio,
			};
		});
	}, []);

	useEffect(() => {
		const el = elRef.current;
		if (!el) return;

		const onWheel = (e: WheelEvent) => {
			e.preventDefault();
			const rect = el.getBoundingClientRect();
			const ax = e.clientX - rect.left;
			const ay = e.clientY - rect.top;
			// Trackpad pinch-to-zoom sends wheel events with ctrlKey set (Chrome).
			const delta = e.ctrlKey ? -e.deltaY * 0.045 : e.deltaY > 0 ? -0.08 : 0.08;
			setKAtPoint(panRef.current.k + delta, ax, ay);
		};

		el.addEventListener("wheel", onWheel, { passive: false });
		return () => el.removeEventListener("wheel", onWheel);
	}, [setKAtPoint]);

	/** Pinch-to-zoom (mobile). */
	useEffect(() => {
		const el = elRef.current;
		if (!el) return;

		let startDist = 0;
		let pinchActive = false;

		const mid = (a: Touch, b: Touch, rect: DOMRect) => ({
			x: (a.clientX + b.clientX) / 2 - rect.left,
			y: (a.clientY + b.clientY) / 2 - rect.top,
		});

		const onTouchStart = (e: TouchEvent) => {
			if (e.touches.length === 2) {
				startDist = touchDistance(e.touches[0]!, e.touches[1]!);
				pinchActive = startDist > 0;
			}
		};

		const onTouchMove = (e: TouchEvent) => {
			if (e.touches.length !== 2 || !pinchActive) return;
			e.preventDefault();
			const t0 = e.touches[0]!;
			const t1 = e.touches[1]!;
			const d = touchDistance(t0, t1);
			if (startDist <= 0) return;
			const ratio = d / startDist;
			startDist = d;
			const rect = el.getBoundingClientRect();
			const { x, y } = mid(t0, t1, rect);
			setKAtPoint(panRef.current.k * ratio, x, y);
		};

		const onTouchEnd = (e: TouchEvent) => {
			if (e.touches.length < 2) {
				pinchActive = false;
				startDist = 0;
			}
		};

		el.addEventListener("touchstart", onTouchStart, { passive: true });
		el.addEventListener("touchmove", onTouchMove, { passive: false });
		el.addEventListener("touchend", onTouchEnd, { passive: true });
		el.addEventListener("touchcancel", onTouchEnd);
		return () => {
			el.removeEventListener("touchstart", onTouchStart);
			el.removeEventListener("touchmove", onTouchMove);
			el.removeEventListener("touchend", onTouchEnd);
			el.removeEventListener("touchcancel", onTouchEnd);
		};
	}, [setKAtPoint]);

	const onPointerDown = useCallback((e: React.PointerEvent) => {
		if (e.button !== 0 && e.pointerType === "mouse") return;
		(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
		dragRef.current = {
			active: true,
			pointerId: e.pointerId,
			startX: e.clientX,
			startY: e.clientY,
			lastX: e.clientX,
			lastY: e.clientY,
			didDrag: false,
			startTarget: e.target as Element,
		};
	}, []);

	const onPointerMove = useCallback((e: React.PointerEvent) => {
		const d = dragRef.current;
		if (!d?.active || d.pointerId !== e.pointerId) return;

		if (!d.didDrag) {
			const fromStart = Math.hypot(e.clientX - d.startX, e.clientY - d.startY);
			if (fromStart <= TAP_MOVE_PX) return;
			d.didDrag = true;
			d.lastX = e.clientX;
			d.lastY = e.clientY;
			return;
		}

		const dx = e.clientX - d.lastX;
		const dy = e.clientY - d.lastY;
		d.lastX = e.clientX;
		d.lastY = e.clientY;
		setPan((p) => ({ ...p, x: p.x + dx, y: p.y + dy }));
	}, []);

	const finishPointer = useCallback((e: React.PointerEvent) => {
		const d = dragRef.current;
		if (!d?.active || d.pointerId !== e.pointerId) return;
		dragRef.current = null;

		if (!d.didDrag) {
			onShortPointerUpRef.current?.({ startTarget: d.startTarget });
		}
	}, []);

	const onPointerUp = useCallback(
		(e: React.PointerEvent) => {
			finishPointer(e);
		},
		[finishPointer],
	);

	const onPointerCancel = useCallback(
		(e: React.PointerEvent) => {
			finishPointer(e);
		},
		[finishPointer],
	);

	return {
		elRef,
		pan,
		setPan,
		handlers: {
			onPointerDown,
			onPointerMove,
			onPointerUp,
			onPointerCancel,
		},
	};
}
